import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { TURNSTILE_SITE_KEY } from '../lib/captcha';

/**
 * Captcha das telas de entrada — login, cadastro e "esqueci a senha" (04/11/2026).
 *
 * Cloudflare Turnstile: na maioria das vezes ninguém clica em nada, ele se resolve em ~1s. Nasceu
 * da varredura de 04/11, que fez cadastro, login e redefinição de senha por script, trocando de IP.
 *
 * A API confere o token no header `X-Captcha-Token` (ver `cabecalhoCaptcha` em lib/captcha). Cada token vale UMA
 * vez: depois de cada envio, chame `reiniciar()` para o widget gerar outro.
 *
 * Sem `VITE_TURNSTILE_SITE_KEY` o componente não aparece e `captchaAtivo` é false — o backend sem
 * a secret também não cobra, então o ambiente sem chave segue funcionando.
 */

const SITE_KEY = TURNSTILE_SITE_KEY;
const URL_SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

interface Turnstile {
    render: (alvo: HTMLElement, opcoes: Record<string, unknown>) => string;
    reset: (id: string) => void;
    remove: (id: string) => void;
}

declare global {
    interface Window {
        turnstile?: Turnstile;
    }
}

let carregamento: Promise<Turnstile> | null = null;

/* Um script só para a aba inteira, mesmo indo do login ao cadastro e voltando. */
const carregarTurnstile = (): Promise<Turnstile> => {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (carregamento) return carregamento;

    carregamento = new Promise((resolver, rejeitar) => {
        const script = document.createElement('script');
        script.src = URL_SCRIPT;
        script.async = true;
        script.onload = () => (window.turnstile ? resolver(window.turnstile) : rejeitar(new Error('Turnstile ausente')));
        script.onerror = () => {
            carregamento = null;
            rejeitar(new Error('Não carregou o Turnstile'));
        };
        document.head.appendChild(script);
    });
    return carregamento;
};

export interface CaptchaHandle {
    reiniciar: () => void;
}

interface Props {
    /** Token novo, ou null quando venceu/deu erro e precisa esperar outro. */
    onToken: (token: string | null) => void;
}

const Captcha = forwardRef<CaptchaHandle, Props>(({ onToken }, ref) => {
    const alvo = useRef<HTMLDivElement>(null);
    const widget = useRef<string | null>(null);
    const aoToken = useRef(onToken);
    aoToken.current = onToken;

    useImperativeHandle(ref, () => ({
        reiniciar: () => {
            aoToken.current(null);
            if (widget.current && window.turnstile) window.turnstile.reset(widget.current);
        },
    }), []);

    useEffect(() => {
        if (!SITE_KEY) return;
        let vivo = true;

        carregarTurnstile()
            .then(turnstile => {
                if (!vivo || !alvo.current) return;
                widget.current = turnstile.render(alvo.current, {
                    sitekey: SITE_KEY,
                    theme: 'light', // a casca das telas de entrada é sempre clara
                    size: 'flexible',
                    language: 'pt-br',
                    callback: (token: string) => aoToken.current(token),
                    'expired-callback': () => aoToken.current(null),
                    'error-callback': () => aoToken.current(null),
                });
            })
            .catch(() => {
                /* Sem o script (bloqueador, rede), a API recusa o envio com a mensagem de
                   captcha — a pessoa vê o motivo lá, não um erro solto aqui. */
            });

        return () => {
            vivo = false;
            if (widget.current && window.turnstile) window.turnstile.remove(widget.current);
            widget.current = null;
        };
    }, []);

    if (!SITE_KEY) return null;
    return <div ref={alvo} className="au-captcha" />;
});

Captcha.displayName = 'Captcha';

export default Captcha;
